import { api } from './api';
import * as ReportTypes from '../types/report.types';

// Types for report responses

// Ensure this is treated as a module
export { };
export interface PatientDemographicsResponse {
  age_distribution: Array<{
    age_group: string;
    count: number;
  }>;
  geographic_distribution: Array<{
    judet: string;
    count: number;
  }>;
}

export interface HospitalStatisticsResponse {
  total_patients: number;
  gender_distribution: Array<{
    gender: number;
    count: number;
  }>;
  birth_weight_stats: {
    avg_birth_weight: number;
    min_birth_weight: number;
    max_birth_weight: number;
  };
  gestational_age_stats: {
    avg_gestational_age: number;
    min_gestational_age: number;
    max_gestational_age: number;
  };
  patients_with_diagnoses: number;
  total_diagnoses: number;
  monthly_trend: Array<{
    month: string;
    count: number;
  }>;
}

export interface ChdDistributionResponse {
  code: string;
  description: string;
  eurocat_category: string;
  count: number;
  percentage: number;
}

export interface SeverityStatisticsResponse {
  severity_id: number;
  level_name: string;
  description: string;
  count: number;
  percentage: number;
}

export interface BirthWeightAnalysisResponse {
  categories: Array<{
    weight_category: string;
    count: number;
    percentage: number;
  }>;
  by_gestational_age: Array<{
    gestational_age: number;
    avg_birth_weight: number;
    count: number;
  }>;
}

export interface MaternalRiskFactorsResponse {
  avg_maternal_age: number;
  teen_mothers: number;
  advanced_age_mothers: number;
  with_complications: number;
  with_medications: number;
  smoking_count: number;
  alcohol_count: number;
  total_with_maternal_data: number;
}

export interface EurocatStatusResponse {
  last_report_date: string | null;
  next_report_date: string;
  pending_cases: number;
  compliance_percentage: number;
  validation_errors: number;
}

export interface CustomReportRequest {
  report_type: 'comprehensive' | 'clinical' | 'demographic' | 'maternal';
  hospital_id?: number;
  start_date?: string;
  end_date?: string;
  filters?: Record<string, any>;
}

export interface ExportRequest {
  report_data: any;
  report_type: string;
  title?: string;
}

export class ReportService {
  /**
   * Get patient demographics report
   */
  async getPatientDemographics(hospitalId?: number, startDate?: string, endDate?: string): Promise<PatientDemographicsResponse> {
    const params: any = {};
    if (hospitalId) params.hospital_id = hospitalId;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;

    const response = await api.get<PatientDemographicsResponse>('/api/reports/patient-demographics', {
      params
    });

    if (!response.data) {
      throw new Error('Nu s-au putut încărca datele demografice pentru pacienți');
    }

    return response.data;
  }

  /**
   * Get hospital statistics report
   */
  async getHospitalStatistics(hospitalId?: number, startDate?: string, endDate?: string): Promise<HospitalStatisticsResponse> {
    const params: any = {};
    if (hospitalId) params.hospital_id = hospitalId;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;

    const response = await api.get<HospitalStatisticsResponse>('/api/reports/hospital', {
      params
    });

    if (!response.data) {
      throw new Error('Nu s-au putut încărca statisticile spitalului');
    }

    return response.data;
  }

  /**
   * Get MCC distribution report
   */
  async getChdDistribution(hospitalId?: number, startDate?: string, endDate?: string): Promise<ChdDistributionResponse[]> {
    const params: any = {};
    if (hospitalId) params.hospital_id = hospitalId;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;

    const response = await api.get<ChdDistributionResponse[]>('/api/reports/chd-distribution', {
      params
    });

    if (!response.data) {
      throw new Error('Nu s-au putut încărca datele de distribuție MCC');
    }

    return response.data;
  }

  /**
   * Get severity statistics report
   */
  async getSeverityStatistics(hospitalId?: number, startDate?: string, endDate?: string): Promise<SeverityStatisticsResponse[]> {
    const params: any = {};
    if (hospitalId) params.hospital_id = hospitalId;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;

    const response = await api.get<SeverityStatisticsResponse[]>('/api/reports/severity-statistics', {
      params
    });

    if (!response.data) {
      throw new Error('Nu s-au putut încărca statisticile de severitate');
    }

    return response.data;
  }

  /**
   * Get birth weight analysis report
   */
  async getBirthWeightAnalysis(hospitalId?: number, startDate?: string, endDate?: string): Promise<BirthWeightAnalysisResponse> {
    const params: any = {};
    if (hospitalId) params.hospital_id = hospitalId;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;

    const response = await api.get<BirthWeightAnalysisResponse>('/api/reports/birth-weight-analysis', {
      params
    });

    if (!response.data) {
      throw new Error('Nu s-a putut încărca analiza greutății la naștere');
    }

    return response.data;
  }

  /**
   * Get maternal risk factors report
   */
  async getMaternalRiskFactors(hospitalId?: number, startDate?: string, endDate?: string): Promise<MaternalRiskFactorsResponse> {
    const params: any = {};
    if (hospitalId) params.hospital_id = hospitalId;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;

    const response = await api.get<MaternalRiskFactorsResponse>('/api/reports/maternal-risk-factors', {
      params
    });

    if (!response.data) {
      throw new Error('Nu s-au putut încărca factorii de risc maternali');
    }

    return response.data;
  }

  /**
   * Get EUROCAT status
   */
  async getEurocatStatus(): Promise<EurocatStatusResponse> {
    const response = await api.get<EurocatStatusResponse>('/api/reports/eurocat/status');

    if (!response.data) {
      throw new Error('Nu s-a putut încărca statusul EUROCAT');
    }

    return response.data;
  }

  /**
   * Generate custom report
   */
  async generateCustomReport(request: CustomReportRequest): Promise<any> {
    const response = await api.post<any>('/api/reports/custom', request);

    if (!response.data) {
      throw new Error('Nu s-a putut genera raportul personalizat');
    }

    return response.data;
  }

  /**
   * Export report to PDF
   */
  async exportToPdf(request: ExportRequest): Promise<Blob> {
    const response = await api.post('/api/reports/export/pdf', request, {
      responseType: 'blob'
    });

    return response as unknown as Blob;
  }

  /**
   * Export report to Excel
   */
  async exportToExcel(request: ExportRequest): Promise<Blob> {
    const response = await api.post('/api/reports/export/excel', request, {
      responseType: 'blob'
    });

    return response as unknown as Blob;
  }

  /**
   * Generate EUROCAT export
   */
  async generateEurocatExport(startDate?: string, endDate?: string, format: string = 'edmp', anonymize: boolean = true): Promise<Blob> {
    const params: any = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    params.format = format;
    params.anonymize = anonymize;

    const response = await api.get('/api/reports/eurocat', {
      params,
      responseType: 'blob'
    });

    return response as unknown as Blob;
  }

  /**
   * Download file helper method
   */
  public downloadFile(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }

  /**
   * Export patient demographics to PDF
   */
  async exportPatientDemographicsToPdf(hospitalId?: number, startDate?: string, endDate?: string, title?: string): Promise<void> {
    try {
      const demographicsData = await this.getPatientDemographics(hospitalId, startDate, endDate);
      const blob = await this.exportToPdf({
        report_data: demographicsData,
        report_type: 'patient_demographics',
        title: title || 'Raport Demografic Pacienți'
      });

      this.downloadFile(blob, `patient_demographics_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (error) {
      console.error('Error exporting patient demographics to PDF:', error);
      throw error;
    }
  }

  /**
   * Export patient demographics to Excel
   */
  async exportPatientDemographicsToExcel(hospitalId?: number, startDate?: string, endDate?: string, title?: string): Promise<void> {
    try {
      const demographicsData = await this.getPatientDemographics(hospitalId, startDate, endDate);
      const blob = await this.exportToExcel({
        report_data: demographicsData,
        report_type: 'patient_demographics',
        title: title || 'Raport Demografic Pacienți'
      });

      this.downloadFile(blob, `patient_demographics_${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (error) {
      console.error('Error exporting patient demographics to Excel:', error);
      throw error;
    }
  }

  // ============================================================================
  // NEW REPORT METHODS
  // ============================================================================

  /**
   * 1. Get Patient Clinical Summary
   */
  async getPatientClinicalSummary(patientId: number): Promise<ReportTypes.PatientClinicalSummary> {
    const response = await api.get<ReportTypes.PatientClinicalSummary>(`/api/reports/patient-clinical-summary/${patientId}`);

    if (!response.data) {
      throw new Error('Nu s-au putut încărca datele rezumatului clinic');
    }

    return response.data;
  }

  /**
   * 2. Get Patient Timeline
   */
  async getPatientTimeline(patientId: number): Promise<ReportTypes.PatientTimeline> {
    const response = await api.get<ReportTypes.PatientTimeline>(`/api/reports/patient-timeline/${patientId}`);

    if (!response.data) {
      throw new Error('Nu s-au putut încărca datele cronologiei pacientului');
    }

    return response.data;
  }

  /**
   * 3. Get Maternal Risk Analysis
   */
  async getMaternalRiskAnalysis(filters?: ReportTypes.ReportFilters): Promise<ReportTypes.MaternalRiskAnalysis> {
    const params: any = {};
    if (filters?.hospital_id) params.hospital_id = filters.hospital_id;
    if (filters?.start_date) params.start_date = filters.start_date;
    if (filters?.end_date) params.end_date = filters.end_date;

    const response = await api.get<ReportTypes.MaternalRiskAnalysis>('/api/reports/maternal-risk-analysis', { params });

    if (!response.data) {
      throw new Error('Nu s-au putut încărca datele analizei factorilor de risc matern');
    }

    return response.data;
  }

  /**
   * 4. Get VON Completeness Report
   */
  async getVonCompletenessReport(filters?: ReportTypes.ReportFilters): Promise<ReportTypes.VonCompletenessReport> {
    const params: any = {};
    if (filters?.hospital_id) params.hospital_id = filters.hospital_id;
    if (filters?.start_date) params.start_date = filters.start_date;
    if (filters?.end_date) params.end_date = filters.end_date;

    const response = await api.get<ReportTypes.VonCompletenessReport>('/api/reports/von-completeness', { params });

    if (!response.data) {
      throw new Error('Nu s-au putut încărca datele raportului de completitudine RONN');
    }

    return response.data;
  }

  /**
   * 5. Get 30-Day Outcomes Analysis
   * @see /docs/apicalls/hospital-reports-api.md for complete API documentation
   */
  async getOutcomesAnalysis(filters?: ReportTypes.ReportFilters): Promise<ReportTypes.ThirtyDayOutcomesReport> {
    const params: any = {};
    if (filters?.hospital_id) params.hospital_id = filters.hospital_id;
    if (filters?.start_date) params.start_date = filters.start_date;
    if (filters?.end_date) params.end_date = filters.end_date;

    const response = await api.get<ReportTypes.ThirtyDayOutcomesReport>('/api/reports/30-day-outcomes', { params });

    if (!response.success) {
      throw new Error(response.error || 'Nu s-au putut încărca datele analizei rezultatelor la 30 de zile');
    }

    if (!response.data) {
      throw new Error('Nu s-au putut încărca datele analizei rezultatelor la 30 de zile');
    }

    // Validate that arrays exist (they may be empty)
    if (!Array.isArray(response.data.outcomes_by_diagnosis)) {
      response.data.outcomes_by_diagnosis = [];
    }
    if (!Array.isArray(response.data.outcomes_by_procedure)) {
      response.data.outcomes_by_procedure = [];
    }
    if (!Array.isArray(response.data.complications)) {
      response.data.complications = [];
    }
    if (!Array.isArray(response.data.readmissions)) {
      response.data.readmissions = [];
    }
    if (!Array.isArray(response.data.detailed_patients)) {
      response.data.detailed_patients = [];
    }

    return response.data;
  }

  /**
   * Alias method for backward compatibility
   */
  async get30DayOutcomesReport(filters?: ReportTypes.ReportFilters): Promise<ReportTypes.ThirtyDayOutcomesReport> {
    return this.getOutcomesAnalysis(filters);
  }

  /**
   * 6. Get Procedure Outcomes Report
   * @see /docs/apicalls/hospital-reports-api.md for complete API documentation
   */
  async getProcedureOutcomesReport(filters?: ReportTypes.ReportFilters): Promise<ReportTypes.ProcedureOutcomesReport> {
    const params: any = {};
    if (filters?.hospital_id) params.hospital_id = filters.hospital_id;
    if (filters?.start_date) params.start_date = filters.start_date;
    if (filters?.end_date) params.end_date = filters.end_date;
    if (filters?.procedure_type_id) params.procedure_type_id = filters.procedure_type_id;

    const response = await api.get<ReportTypes.ProcedureOutcomesReport>('/api/reports/procedure-outcomes', { params });

    if (!response.success) {
      throw new Error(response.error || 'Nu s-au putut încărca datele raportului de rezultate proceduri');
    }

    if (!response.data) {
      throw new Error('Nu s-au putut încărca datele raportului de rezultate proceduri');
    }

    // Check for backend error in response
    if (response.data.error) {
      throw new Error(response.data.error);
    }

    // Validate that arrays exist (they may be empty)
    if (!Array.isArray(response.data.by_procedure_type)) {
      response.data.by_procedure_type = [];
    }
    if (response.data.by_surgeon && !Array.isArray(response.data.by_surgeon)) {
      response.data.by_surgeon = [];
    }
    if (!response.data.complications_breakdown || typeof response.data.complications_breakdown !== 'object') {
      response.data.complications_breakdown = {};
    }

    return response.data;
  }

  /**
   * 7. Get Premature Statistics
   */
  async getPrematureStatistics(filters?: ReportTypes.ReportFilters): Promise<ReportTypes.PrematureStatistics> {
    const params: any = {};
    if (filters?.hospital_id) params.hospital_id = filters.hospital_id;
    if (filters?.start_date) params.start_date = filters.start_date;
    if (filters?.end_date) params.end_date = filters.end_date;

    const response = await api.get<ReportTypes.PrematureStatistics>('/api/reports/premature-statistics', { params });

    if (!response.data) {
      throw new Error('Nu s-au putut încărca statisticile pentru pacienții prematuri');
    }

    return response.data;
  }

  /**
   * 8. Get VON Compliance Report
   */
  async getVonComplianceReport(filters?: ReportTypes.ReportFilters): Promise<ReportTypes.VonComplianceReport> {
    const params: any = {};
    if (filters?.hospital_id) params.hospital_id = filters.hospital_id;
    if (filters?.start_date) params.start_date = filters.start_date;
    if (filters?.end_date) params.end_date = filters.end_date;

    const response = await api.get<ReportTypes.VonComplianceReport>('/api/reports/von-compliance', { params });

    if (!response.data) {
      throw new Error('Nu s-au putut încărca datele raportului de conformitate RONN');
    }

    return response.data;
  }

  /**
   * 9. Get Trends Analysis
   */
  async getTrendsAnalysis(period: 'monthly' | 'yearly', filters?: ReportTypes.ReportFilters): Promise<ReportTypes.TrendsAnalysis> {
    const params: any = { period };
    if (filters?.hospital_id) params.hospital_id = filters.hospital_id;
    if (filters?.start_date) params.start_date = filters.start_date;
    if (filters?.end_date) params.end_date = filters.end_date;

    const response = await api.get<ReportTypes.TrendsAnalysis>('/api/reports/trends', { params });

    if (!response.data) {
      throw new Error('Nu s-au putut încărca datele analizei tendințelor');
    }

    return response.data;
  }

  /**
   * 10. Get Quality Indicators
   */
  async getQualityIndicators(filters?: ReportTypes.ReportFilters): Promise<ReportTypes.QualityIndicators> {
    const params: any = {};
    if (filters?.hospital_id) params.hospital_id = filters.hospital_id;
    if (filters?.start_date) params.start_date = filters.start_date;
    if (filters?.end_date) params.end_date = filters.end_date;

    const response = await api.get<ReportTypes.QualityIndicators>('/api/reports/quality-indicators', { params });

    if (!response.data) {
      throw new Error('Nu s-au putut încărca indicatorii de calitate');
    }

    return response.data;
  }

  /**
   * 11. Get Comparative Analysis (Admin Only)
   */
  async getComparativeAnalysis(filters?: ReportTypes.ReportFilters): Promise<ReportTypes.ComparativeAnalysis> {
    const params: any = {};
    if (filters?.start_date) params.start_date = filters.start_date;
    if (filters?.end_date) params.end_date = filters.end_date;

    const response = await api.get<ReportTypes.ComparativeAnalysis>('/api/reports/comparative-analysis', { params });

    if (!response.data) {
      throw new Error('Nu s-au putut încărca datele analizei comparative');
    }

    return response.data;
  }
}

// Export singleton instance
export const reportService = new ReportService();